// @avantia common-code
import { SMap } from '@avantia/client-and-server-utilities';
import { lodash } from '@avantia/lodash';
import { recordElapsedTime } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { getQuestionIdFor } from '../tools/questionIds';
import { HpClientQuestionSetState, HpQuestionIdGroup, HpQuestionIdNode, specialQuestionIds } from '../types';
import { applyChanges } from './reducerLibrary';

const swappedOut: SMap<SMap<unknown>> = {};

// These questions should always exist, or we won't be able to change navigation, etc.
const alwaysIncludeList = [...specialQuestionIds];

let optimisationEnabled: boolean;
registerConfigurationAction((config) => {
  ({ optimisationEnabled } = config);
});

export function addQuestionIdToAlwaysIncludeList(questionNames: string[]): void {
  alwaysIncludeList.push(...questionNames);
}

export function swapOutQuestions(state: HpClientQuestionSetState): HpClientQuestionSetState {
  if (!optimisationEnabled) {
    return state;
  }

  return recordElapsedTime('swapOutQuestions', () => {
    const { displayState, productId, questions: origQuestions } = state;
    const { allOrderedQuestionIds, visibleQuestionIds } = displayState;

    if (visibleQuestionIds.length === 0 && allOrderedQuestionIds.length === 0) {
      return state;
    }

    const keysInUse: SMap<true> = {};

    function addKey(key: string): void {
      keysInUse[key] = true;
    }

    visibleQuestionIds.forEach((vid: HpQuestionIdNode) => {
      const qs = (vid as HpQuestionIdGroup).questions;
      if (qs) {
        addGroupKeys(vid);
      } else {
        addKey(vid as string);
      }
    });

    allOrderedQuestionIds.forEach((id) => {
      if (origQuestions[id].groupId) {
        addKey(id);
      }
    });

    // some of the items in the alwaysIncludeList (e.g. 'missingOptions') don't exist in all questionsets
    // hence the 'false' argument to 'getQuestionIdFor'.
    alwaysIncludeList
      .map((id) => getQuestionIdFor(productId, id, false))
      .forEach((id) => {
        if (id) {
          addKey(id);
        }
      });

    const { map: questions } = swapOut('questions', origQuestions, keysInUse);
    return applyChanges(state, { questions });

    function addGroupKeys(vid: HpQuestionIdNode): void {
      const qs = (vid as HpQuestionIdGroup).questions;
      if (qs) {
        qs.forEach((q) => {
          if (typeof q === 'string') {
            addKey(q);
          } else {
            addGroupKeys(q);
          }
        });
      } else {
        addKey(vid as string);
      }
    }
  });
}

export function swapInQuestions(state: HpClientQuestionSetState): HpClientQuestionSetState {
  if (!optimisationEnabled) {
    return state;
  }

  return recordElapsedTime('swapInQuestions', () => {
    const { questions: origQuestions } = state;
    const {
      map: questions,
      origCount: origQuestionCount,
      newCount: newQuestionCount
    } = swapIn('questions', origQuestions);

    if (origQuestionCount + newQuestionCount === 0) {
      return state;
    }

    return applyChanges(state, { questions });
  });
}

export function swapOut<ItemT>(name: string, map: SMap<ItemT>, keysInUse: SMap<true>): HpSwapResult<ItemT> {
  const swapOut: SMap<ItemT> = {};
  const keep: SMap<ItemT> = {};
  let origCount = 0;
  let newCount = 0;

  for (const key in map) {
    origCount++;
    if (!keysInUse[key]) {
      swapOut[key] = map[key];
    } else {
      keep[key] = map[key];
      newCount++;
    }
  }

  swappedOut[name] = swapOut;
  return { map: keep, origCount, newCount };
}

export interface HpSwapResult<ItemT> {
  map: SMap<ItemT>;
  origCount: number;
  newCount: number;
}

export function swapIn<ItemT>(name: string, map: SMap<ItemT>): HpSwapResult<ItemT> {
  const toSwapIn = swappedOut[name] as SMap<ItemT> | undefined;
  let origCount = 0;
  let newCount = 0;

  if (!toSwapIn) {
    return { map, origCount: lodash.keys(map).length, newCount };
  }

  const all: SMap<ItemT> = {};

  for (const key in map) {
    all[key] = map[key];
    origCount++;
  }

  newCount = origCount;

  if (toSwapIn) {
    for (const key in toSwapIn) {
      all[key] = toSwapIn[key];
      newCount++;
    }
  }

  return { map: all, origCount, newCount };
}
